import * as React from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SubCard from 'ui-component/cards/SubCard';
import Customer from 'types/customer';
import { getRepLinkFlags } from '../../../utils/repLinkFlags';
import {
    createClientEditLink,
    listClientDataDocs,
    clientDataDocUrl,
    getConfiguredClientSignDocs,
    uploadClientDataDoc,
    ClientSignDocDef,
} from '../../../api/customer/externalLinks.api';

function CopyableLink({ url }: { url: string }) {
    const [copied, setCopied] = React.useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            /* ignore */
        }
    };
    return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
            <TextField fullWidth size="small" value={url} InputProps={{ readOnly: true }} dir="ltr" />
            <Tooltip title={copied ? 'הועתק!' : 'העתק'}>
                <IconButton onClick={copy} color={copied ? 'success' : 'default'}>
                    <ContentCopyIcon />
                </IconButton>
            </Tooltip>
            <Tooltip title="פתח">
                <IconButton onClick={() => window.open(url, '_blank')}>
                    <OpenInNewIcon />
                </IconButton>
            </Tooltip>
        </Stack>
    );
}

function DocSlot({
    clientId,
    doc,
    uploaded,
    cacheBust,
    onUploaded,
    onPreview,
}: {
    clientId: number | string;
    doc: ClientSignDocDef;
    uploaded: boolean;
    cacheBust: number;
    onUploaded: () => void;
    onPreview: (url: string, title: string) => void;
}) {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const url = clientDataDocUrl(clientId, doc.key, cacheBust);

    const onFile = async (file: File | undefined) => {
        if (!file) return;
        setUploading(true);
        setError(null);
        try {
            await uploadClientDataDoc(clientId, doc.key, file);
            onUploaded();
        } catch (e: any) {
            setError(e?.message || 'העלאה נכשלה');
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = '';
        }
    };

    return (
        <Box
            sx={{
                width: 160,
                p: 1,
                border: '1px solid',
                borderColor: uploaded ? 'success.light' : 'divider',
                borderRadius: 2,
                textAlign: 'center',
            }}
        >
            {uploaded ? (
                <Box
                    component="button"
                    type="button"
                    onClick={() => onPreview(url, doc.label || doc.key)}
                    sx={{
                        p: 0,
                        m: 0,
                        border: 0,
                        background: 'none',
                        cursor: 'zoom-in',
                        display: 'block',
                        mx: 'auto',
                    }}
                >
                    <img
                        src={url}
                        alt={doc.label}
                        style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8, display: 'block' }}
                    />
                </Box>
            ) : (
                <Box
                    sx={{
                        width: 120,
                        height: 120,
                        mx: 'auto',
                        borderRadius: 2,
                        bgcolor: 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                    }}
                >
                    <UploadFileIcon fontSize="large" />
                </Box>
            )}
            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" sx={{ mt: 0.5 }}>
                {uploaded ? (
                    <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
                ) : (
                    <WarningAmberIcon color="warning" sx={{ fontSize: 16 }} />
                )}
                <Typography variant="caption" fontWeight={600}>
                    {doc.label || doc.key}
                </Typography>
            </Stack>
            {!!doc.required && (
                <Chip label="חובה" size="small" color="warning" sx={{ mt: 0.5, height: 20 }} />
            )}
            <input
                ref={inputRef}
                type="file"
                accept="image/*,application/pdf,.pdf"
                hidden
                onChange={(e) => onFile(e.target.files?.[0])}
            />
            <Button
                size="small"
                variant="outlined"
                sx={{ mt: 1 }}
                disabled={uploading}
                startIcon={uploading ? <CircularProgress size={14} /> : <UploadFileIcon />}
                onClick={() => inputRef.current?.click()}
            >
                {uploaded ? 'החלף' : 'העלה'}
            </Button>
            {error && (
                <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
}

function ClientExternalSection({ customer }: { customer: Customer }) {
    const { clientSignup: showClientSignup } = getRepLinkFlags();

    const [editLink, setEditLink] = React.useState<string>('');
    const [editLoading, setEditLoading] = React.useState(false);

    const requestedDocs = React.useMemo(() => getConfiguredClientSignDocs(), []);
    const [uploadedNames, setUploadedNames] = React.useState<string[]>([]);
    const [cacheBust, setCacheBust] = React.useState(() => Date.now());
    const [docsLoading, setDocsLoading] = React.useState(false);
    const [preview, setPreview] = React.useState<{ url: string; title: string } | null>(null);

    const refreshDocs = React.useCallback(() => {
        if (!showClientSignup || !customer?.id) return;
        setDocsLoading(true);
        listClientDataDocs(customer.id)
            .then(setUploadedNames)
            .catch(() => setUploadedNames([]))
            .finally(() => setDocsLoading(false));
    }, [customer?.id, showClientSignup]);

    React.useEffect(() => {
        refreshDocs();
    }, [refreshDocs]);

    if (!showClientSignup) {
        return null;
    }

    const genEdit = async () => {
        if (!customer?.id) return;
        setEditLoading(true);
        try {
            setEditLink(await createClientEditLink(customer.id));
        } catch (e) {
            console.error(e);
        } finally {
            setEditLoading(false);
        }
    };

    const uploadedSet = React.useMemo(() => new Set(uploadedNames), [uploadedNames]);
    const requestedKeys = React.useMemo(() => new Set(requestedDocs.map((d) => d.key)), [requestedDocs]);
    const extraUploaded = uploadedNames.filter((n) => !requestedKeys.has(n));
    const onDocUploaded = () => {
        setCacheBust(Date.now());
        refreshDocs();
    };

    return (
        <Stack spacing={2} sx={{ mt: 2 }}>
            {showClientSignup && !!customer?.id && (
                <SubCard
                    title={
                        <Stack direction="row" spacing={1} alignItems="center">
                            <LinkIcon fontSize="small" />
                            <span>לינק עדכון פרטים ומסמכים</span>
                        </Stack>
                    }
                >
                    <Typography variant="body2" color="textSecondary">
                        שלח ללקוח לינק חד-פעמי (שעה) לעדכון פרטי הכרטיס והעלאת מסמכים חתומים — או העלה כאן בעצמך.
                    </Typography>
                    <Button variant="contained" size="small" sx={{ mt: 1 }} onClick={genEdit} disabled={editLoading}>
                        {editLoading ? 'יוצר...' : 'צור לינק עדכון ללקוח'}
                    </Button>
                    {editLink && <CopyableLink url={editLink} />}

                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                        מסמכים מבוקשים
                        {docsLoading ? '…' : ''}
                    </Typography>
                    {requestedDocs.length === 0 && uploadedNames.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">
                            לא הוגדרו מסמכים ב־clientsign_docs (בקרת לינקים).
                        </Typography>
                    ) : (
                        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                            {[
                                ...requestedDocs,
                                ...(uploadedNames.includes('client_sign') &&
                                !requestedDocs.some((d) => d.key === 'client_sign')
                                    ? [{ key: 'client_sign', label: 'חתימה דיגיטלית', required: 0 }]
                                    : []),
                            ].map((doc) => (
                                <DocSlot
                                    key={doc.key}
                                    clientId={customer.id}
                                    doc={doc}
                                    uploaded={uploadedSet.has(doc.key)}
                                    cacheBust={cacheBust}
                                    onUploaded={onDocUploaded}
                                    onPreview={(url, title) => setPreview({ url, title })}
                                />
                            ))}
                            {extraUploaded
                                .filter((n) => n !== 'client_sign')
                                .map((name) => (
                                <DocSlot
                                    key={name}
                                    clientId={customer.id}
                                    doc={{ key: name, label: name }}
                                    uploaded
                                    cacheBust={cacheBust}
                                    onUploaded={onDocUploaded}
                                    onPreview={(url, title) => setPreview({ url, title })}
                                />
                            ))}
                        </Stack>
                    )}
                </SubCard>
            )}

            <Dialog
                open={Boolean(preview)}
                onClose={() => setPreview(null)}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: { bgcolor: 'rgba(0,0,0,0.92)', boxShadow: 'none' } }}
            >
                <IconButton
                    onClick={() => setPreview(null)}
                    sx={{ position: 'absolute', top: 8, left: 8, color: 'white', zIndex: 1 }}
                    aria-label="סגור"
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent
                    sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    {preview && (
                        <>
                            <Typography color="white" variant="subtitle1">
                                {preview.title}
                            </Typography>
                            <img
                                src={preview.url}
                                alt={preview.title}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '80vh',
                                    objectFit: 'contain',
                                    borderRadius: 8,
                                }}
                            />
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </Stack>
    );
}

export default ClientExternalSection;
